import { NestedOption, NestedOptionType, OptionGroup } from '../types';
import { District, Location, RegionalDivision } from '../types/schema';
import { getInterimDistrictParentId } from './regionLocationHierarchy';

export type RegionDistrictSource = {
    companyId: string;
    companyRegions: RegionalDivision[];
    companyDistricts: District[];
    companyLocations: Location[];
};

export type UserAssignmentSource = {
    regionalAssignment?: Array<string | null> | null;
    locationAssignment?: Array<string | null> | null;
    globalLocationPermission?: boolean | null;
};

export type NestedAssignments = {
    regionalAssignment: string[];
    locationAssignment: string[];
    globalLocationPermission: boolean;
};

/** Prefer interim parent encoding; fall back to residual Gen1 districtId. */
export function getLocationDistrictId(location: Location): string | null {
    return getInterimDistrictParentId(
        location as Location & { districtId?: string | null },
    );
}

function companyScopedRegions(
    companyRegions: RegionalDivision[],
    companyId: string,
): RegionalDivision[] {
    return companyRegions.filter((r) => r.companyId === companyId);
}

function locationsForDistrict(companyLocations: Location[], districtId: string): Location[] {
    return companyLocations.filter(
        (loc) => loc.id === districtId || getLocationDistrictId(loc) === districtId,
    );
}

function locationsForRegion(companyLocations: Location[], regionId: string): Location[] {
    return companyLocations.filter((loc) => loc.regionalDivisionId === regionId);
}

/**
 * Build nested OptionGroups for Region → District → Location pickers
 * (Users list + Employment District), including Global Access.
 */
export function buildRegionDistrictLocationOptionGroups({
    companyId,
    companyRegions,
    companyDistricts,
    companyLocations,
}: RegionDistrictSource): OptionGroup[] {
    const options: OptionGroup[] = [
        {
            label: 'Global Access',
            options: [
                {
                    label: 'All Regions, Districts, and Locations',
                    value: '*',
                    type: 'all' as NestedOptionType,
                },
            ],
        },
    ];

    const districtIdSet = new Set(companyDistricts.map((d) => d.id));

    companyScopedRegions(companyRegions, companyId).forEach((region) => {
        const regionOptions: NestedOption[] = [
            {
                label: `All of ${region.name}`,
                value: region.id,
                type: 'region' as NestedOptionType,
            },
        ];

        const regionDistricts = companyDistricts.filter((d) => d.regionalDivisionId === region.id);

        regionDistricts.forEach((district) => {
            regionOptions.push({
                label: `All of ${district.name}`,
                value: `${region.id}:${district.id}`,
                type: 'district' as NestedOptionType,
            });

            companyLocations
                .filter((loc) => getLocationDistrictId(loc) === district.id && loc.id !== district.id)
                .forEach((location) => {
                    regionOptions.push({
                        label: `  ${location.name}`,
                        value: `${region.id}:${district.id}:${location.id}`,
                        type: 'location' as NestedOptionType,
                    });
                });
        });

        // Locations under the region that are neither districts nor nested under a district
        companyLocations
            .filter(
                (loc) =>
                    loc.regionalDivisionId === region.id &&
                    !getLocationDistrictId(loc) &&
                    !districtIdSet.has(loc.id),
            )
            .forEach((location) => {
                regionOptions.push({
                    label: location.name,
                    value: `${region.id}::${location.id}`,
                    type: 'location' as NestedOptionType,
                });
            });

        options.push({ label: region.name, options: regionOptions });
    });

    return options;
}

/**
 * Convert nested picker selection into User regional/location assignment fields.
 */
export function nestedOptionsToAssignments(
    selected: NestedOption[],
    {
        companyId,
        companyRegions,
        companyLocations,
    }: Pick<RegionDistrictSource, 'companyId' | 'companyRegions' | 'companyLocations'>,
): NestedAssignments {
    const regionalAssignment: string[] = [];
    const locationAssignment: string[] = [];
    const globalLocationPermission = selected.some((o) => o.type === 'all');

    if (globalLocationPermission) {
        companyScopedRegions(companyRegions, companyId).forEach((r) => regionalAssignment.push(r.id));
        companyLocations.forEach((loc) => locationAssignment.push(loc.id));
        return { regionalAssignment, locationAssignment, globalLocationPermission };
    }

    selected.forEach((option) => {
        if (option.type === 'region') {
            if (!regionalAssignment.includes(option.value)) {
                regionalAssignment.push(option.value);
            }
            locationsForRegion(companyLocations, option.value).forEach((loc) => {
                if (!locationAssignment.includes(loc.id)) {
                    locationAssignment.push(loc.id);
                }
            });
        } else if (option.type === 'district') {
            const [regionId, districtId] = option.value.split(':');
            if (regionId && !regionalAssignment.includes(regionId)) {
                regionalAssignment.push(regionId);
            }
            if (districtId) {
                locationsForDistrict(companyLocations, districtId).forEach((loc) => {
                    if (!locationAssignment.includes(loc.id)) {
                        locationAssignment.push(loc.id);
                    }
                });
            }
        } else if (option.type === 'location') {
            const parts = option.value.split(':');
            const regionId = parts[0];
            const locationId = parts[parts.length - 1];
            if (regionId && !regionalAssignment.includes(regionId)) {
                regionalAssignment.push(regionId);
            }
            if (locationId && !locationAssignment.includes(locationId)) {
                locationAssignment.push(locationId);
            }
        }
    });

    return { regionalAssignment, locationAssignment, globalLocationPermission };
}

/**
 * Rebuild nested picker selection from a user's stored assignment fields.
 */
export function assignmentsToNestedOptions(
    user: UserAssignmentSource,
    {
        companyRegions,
        companyDistricts,
        companyLocations,
    }: RegionDistrictSource,
): NestedOption[] {
    const existingOptions: NestedOption[] = [];
    const userRegions = (user.regionalAssignment || []).filter(Boolean) as string[];
    const userLocations = (user.locationAssignment || []).filter(Boolean) as string[];

    const hasAllAccess = Boolean(user.globalLocationPermission);

    if (hasAllAccess) {
        existingOptions.push({
            label: 'All Regions, Districts, and Locations',
            value: '*',
            type: 'all' as NestedOptionType,
        });
        return existingOptions;
    }

    userRegions.forEach((regionId) => {
        const regionLocations = locationsForRegion(companyLocations, regionId);
        const allRegionLocationsAssigned =
            regionLocations.length > 0 && regionLocations.every((loc) => userLocations.includes(loc.id));

        if (allRegionLocationsAssigned) {
            const region = companyRegions.find((r) => r.id === regionId);
            existingOptions.push({
                label: `All of ${region?.name || regionId}`,
                value: regionId,
                type: 'region' as NestedOptionType,
            });
            return;
        }

        const regionDistricts = companyDistricts.filter((d) => d.regionalDivisionId === regionId);
        const districtIdSet = new Set(regionDistricts.map((d) => d.id));

        regionDistricts.forEach((district) => {
            const districtLocations = locationsForDistrict(companyLocations, district.id);
            const allDistrictLocationsAssigned =
                districtLocations.length > 0 &&
                districtLocations.every((loc) => userLocations.includes(loc.id));

            if (allDistrictLocationsAssigned) {
                existingOptions.push({
                    label: `All of ${district.name}`,
                    value: `${regionId}:${district.id}`,
                    type: 'district' as NestedOptionType,
                });
            } else {
                districtLocations
                    .filter((loc) => loc.id !== district.id && userLocations.includes(loc.id))
                    .forEach((loc) => {
                        existingOptions.push({
                            label: `  ${loc.name}`,
                            value: `${regionId}:${district.id}:${loc.id}`,
                            type: 'location' as NestedOptionType,
                        });
                    });
            }
        });

        companyLocations
            .filter(
                (loc) =>
                    loc.regionalDivisionId === regionId &&
                    !getLocationDistrictId(loc) &&
                    !districtIdSet.has(loc.id) &&
                    userLocations.includes(loc.id),
            )
            .forEach((loc) => {
                existingOptions.push({
                    label: loc.name,
                    value: `${regionId}::${loc.id}`,
                    type: 'location' as NestedOptionType,
                });
            });
    });

    return existingOptions;
}

/**
 * Parent/child toggle for nested region/district/location checkboxes.
 */
export function toggleNestedRegionOption(
    option: NestedOption,
    selected: NestedOption[],
    optionGroups: OptionGroup[],
): NestedOption[] {
    if (option.type === 'all') {
        const hasAll = selected.some((o) => o.type === 'all');
        return hasAll ? [] : [option];
    }

    const exists = selected.find((o) => o.value === option.value);

    if (option.type === 'region') {
        const regionGroup = optionGroups.find((g) =>
            g.options.some((o) => o.value === option.value && o.type === 'region'),
        );
        const childValues =
            regionGroup?.options
                .filter((o) => o.type === 'district' || o.type === 'location')
                .map((o) => o.value) || [];

        if (exists) {
            return selected.filter(
                (o) => o.value !== option.value && !childValues.includes(o.value) && o.type !== 'all',
            );
        }

        const childOptions: NestedOption[] =
            regionGroup?.options
                .filter((o) => o.type === 'district' || o.type === 'location')
                .map((o) => ({
                    label: o.label,
                    value: o.value,
                    type: o.type as NestedOptionType,
                })) || [];

        return [
            ...selected.filter(
                (o) =>
                    o.type !== 'all' &&
                    o.value !== option.value &&
                    !childValues.includes(o.value),
            ),
            option,
            ...childOptions,
        ];
    }

    if (option.type === 'district') {
        const locationChildren: NestedOption[] = [];
        for (const group of optionGroups) {
            const districtIdx = group.options.findIndex(
                (o) => o.value === option.value && o.type === 'district',
            );
            if (districtIdx >= 0) {
                for (let i = districtIdx + 1; i < group.options.length; i++) {
                    if (group.options[i].type === 'district' || group.options[i].type === 'region') {
                        break;
                    }
                    if (group.options[i].type === 'location') {
                        locationChildren.push({
                            label: group.options[i].label,
                            value: group.options[i].value,
                            type: 'location' as NestedOptionType,
                        });
                    }
                }
                break;
            }
        }
        const childValues = locationChildren.map((c) => c.value);

        if (exists) {
            return selected.filter(
                (o) => o.value !== option.value && !childValues.includes(o.value) && o.type !== 'all',
            );
        }

        return [
            ...selected.filter(
                (o) =>
                    o.type !== 'all' &&
                    o.value !== option.value &&
                    !childValues.includes(o.value),
            ),
            option,
            ...locationChildren,
        ];
    }

    // Location — simple toggle
    if (exists) {
        return selected.filter((o) => o.value !== option.value && o.type !== 'all');
    }
    return [...selected.filter((o) => o.type !== 'all'), option];
}

/** Human-readable summary of selected nested options for display mode. */
export function formatNestedSelectionLabel(selected: NestedOption[]): string {
    if (selected.length === 0) return '';
    if (selected.some((o) => o.type === 'all')) {
        return 'All Regions, Districts, and Locations';
    }

    const labels = selected
        .filter((o) => o.type === 'region' || o.type === 'district')
        .map((o) => o.label.replace(/^All of /, '').trim());

    if (labels.length > 0) {
        return labels.join(', ');
    }

    return selected.map((o) => o.label.trim()).join(', ');
}
